import React, { useState, useEffect, useRef } from 'react';
import { Button } from '../ui/button';
import { 
  MessageCircle, X as CloseIcon, Send, Sparkles, User, Bot, 
  Ticket, Phone, Mail, Clock, CheckCircle2, ArrowRight
} from 'lucide-react';
import { toast } from 'sonner';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

const ChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  
  // Conversational flow state
  const [flowStep, setFlowStep] = useState('welcome'); // welcome, service_type, details, plan_interest, final
  const [userData, setUserData] = useState({
    name: '',
    mobile: '',
    email: '',
    city: '',
    service_type: '',
    plan_interest: '',
    issue_description: ''
  });
  
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Poll for new messages when in human chat mode
  useEffect(() => {
    if (!sessionId || flowStep !== 'human_chat') return;

    const pollMessages = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/chat/messages/${sessionId}`);
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.messages) {
            // Update messages if we have new ones from support
            setMessages(data.messages);
          }
        }
      } catch (error) {
        console.error('Error polling messages:', error);
      }
    };

    // Initial fetch
    pollMessages();

    // Poll every 3 seconds for new messages from support
    const interval = setInterval(pollMessages, 3000);
    
    return () => clearInterval(interval);
  }, [sessionId, flowStep]);

  const startSession = async () => {
    try {
      const visitorId = localStorage.getItem('netix_visitor_id') || 
        `visitor_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      localStorage.setItem('netix_visitor_id', visitorId);

      const response = await fetch(`${API_BASE_URL}/api/chat/start-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ visitor_id: visitorId })
      });

      const data = await response.json();
      
      if (data.success) {
        setSessionId(data.session_id);
        // Add welcome message with service type buttons
        addBotMessage(
          "👋 Hello! Welcome to **Netix Fibernet**!\n\nI'm Netix AI. Ask me anything about plans, coverage or recharge — or pick an option below:",
          true,
          'service_type_buttons'
        );
      }
    } catch (error) {
      console.error('Error starting session:', error);
      toast.error('Failed to start chat');
    }
  };

  const addBotMessage = (text, isBot = true, showButtons = null) => {
    const newMessage = {
      message_id: `msg_${Date.now()}`,
      message: text,
      sender_type: isBot ? 'bot' : 'visitor',
      sender_name: isBot ? 'Netix Assistant' : 'You',
      timestamp: new Date().toISOString(),
      showButtons: showButtons
    };
    setMessages(prev => [...prev, newMessage]);
  };

  const addUserMessage = (text) => {
    const newMessage = {
      message_id: `msg_${Date.now()}`,
      message: text,
      sender_type: 'visitor',
      sender_name: 'You',
      timestamp: new Date().toISOString()
    };
    setMessages(prev => [...prev, newMessage]);
  };

  const handleServiceTypeSelection = (serviceType) => {
    setUserData(prev => ({ ...prev, service_type: serviceType }));
    addUserMessage(serviceType);
    
    if (serviceType === 'Ask Netix AI') {
      setFlowStep('ai_chat');
      addBotMessage("Sure! Ask me about broadband plans, lease lines, coverage in your city, recharge validity or payments. 💬");
      return;
    }
    if (serviceType === 'New Connection') {
      setFlowStep('location');
      addBotMessage("Great choice! 🎉\n\nWhat's your location? Please enter your city or area:");
    } else if (serviceType === 'Existing Customer') {
      setFlowStep('details');
      addBotMessage("Thanks for being with us! 🙏\n\nLet me help you. What's your name?");
    } else if (serviceType === 'Technical Support') {
      setFlowStep('details');
      addBotMessage("I'll help you with technical support. 🔧\n\nFirst, what's your name?");
    } else if (serviceType === 'Billing') {
      setFlowStep('details');
      addBotMessage("Let me assist you with billing. 💳\n\nWhat's your name?");
    }
  };

  const handleLocationInput = (city) => {
    setUserData(prev => ({ ...prev, city }));
    addUserMessage(city);
    setFlowStep('plan_interest');
    addBotMessage(
      `Perfect! We serve ${city}. 📍\n\nWhich internet plan are you interested in?`,
      true,
      'plan_buttons'
    );
  };

  const handlePlanSelection = (plan) => {
    setUserData(prev => ({ ...prev, plan_interest: plan }));
    addUserMessage(plan);
    setFlowStep('details');
    addBotMessage(`Excellent choice! ${plan} is perfect for ${userData.service_type === 'New Connection' ? 'fast browsing and streaming' : 'your needs'}. 🚀\n\nNow, let me get your details. What's your name?`);
  };

  const handleDetailsFlow = async (value) => {
    addUserMessage(value);

    if (!userData.name) {
      setUserData(prev => ({ ...prev, name: value }));
      addBotMessage(`Nice to meet you, ${value}! 👋\n\nWhat's your mobile number?`);
    } else if (!userData.mobile) {
      // Validate mobile
      if (!/^[6-9]\d{9}$/.test(value)) {
        addBotMessage("Please enter a valid 10-digit mobile number starting with 6-9.");
        return;
      }
      setUserData(prev => ({ ...prev, mobile: value }));
      addBotMessage(`Got it! 📱\n\nWhat's your email address?`);
    } else if (!userData.email) {
      // Validate email
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
        addBotMessage("Please enter a valid email address.");
        return;
      }
      setUserData(prev => ({ ...prev, email: value }));
      
      if (userData.service_type === 'New Connection' && !userData.city) {
        addBotMessage(`Perfect! ✉️\n\nWhat's your city or area?`);
      } else if (!userData.issue_description) {
        addBotMessage(`Great! 📧\n\nPlease describe your ${userData.service_type === 'Technical Support' ? 'technical issue' : userData.service_type === 'Billing' ? 'billing query' : 'requirements'} in detail:`);
      }
    } else if (!userData.city && userData.service_type !== 'New Connection') {
      setUserData(prev => ({ ...prev, city: value }));
      addBotMessage(`Thanks! 📍\n\nPlease describe your ${userData.service_type === 'Technical Support' ? 'technical issue' : userData.service_type === 'Billing' ? 'billing query' : 'requirements'} in detail:`);
    } else if (!userData.issue_description) {
      setUserData(prev => ({ ...prev, issue_description: value }));
      setFlowStep('final');
      addBotMessage(
        `Thank you for sharing! 📝\n\nI have all your details. What would you like to do next?`,
        true,
        'action_buttons'
      );
    }
  };

  const handleCreateTicket = async () => {
    setIsLoading(true);
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/tickets/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...userData,
          session_id: sessionId
        })
      });

      const data = await response.json();
      
      if (data.success) {
        addBotMessage(
          `✅ **Ticket Created Successfully!**\n\n🎫 Ticket Number: **${data.ticket_number}**\n\n📧 A confirmation email has been sent to ${userData.email}\n\n⏱️ **Response Time:** 30 minutes (10 AM - 6 PM)\n📞 **Support:** +91 93199 19988\n☎️ **Office Phone:** 06276 - 459015\n\n🔗 Track your ticket: ${data.tracking_url}`
        );
        toast.success(`Ticket ${data.ticket_number} created!`);
      } else {
        toast.error('Failed to create ticket');
      }
    } catch (error) {
      console.error('Error creating ticket:', error);
      toast.error('Failed to create ticket');
    } finally {
      setIsLoading(false);
    }
  };

  const handleChatWithHuman = async () => {
    setIsLoading(true);
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/chat/handoff-to-human`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: sessionId,
          name: userData.name,
          mobile: userData.mobile,
          email: userData.email,
          city: userData.city,
          connection_type: userData.service_type,
          issue_type: userData.issue_description
        })
      });

      const data = await response.json();
      
      if (data.success) {
        setFlowStep('human_chat');
        addBotMessage(
          `✅ **Connected to Human Support!**\n\n👤 A support agent will respond shortly.\n\n📞 For immediate help, call: +91 93199 19988\n☎️ Office Phone: 06276 - 459015`
        );
        toast.success('Connected to support team!');
      }
    } catch (error) {
      console.error('Error handoff:', error);
      toast.error('Failed to connect to support');
    } finally {
      setIsLoading(false);
    }
  };

  const handleTalkToHuman = () => {
    setUserData(prev => ({ ...prev, service_type: prev.service_type || 'Technical Support' }));
    setFlowStep('details');
    addBotMessage("Sure, I'll connect you to our support team. First, what's your name?");
  };

  const askAI = async (message) => {
    addUserMessage(message);
    setIsLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/chat/ai-reply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: sessionId,
          message,
          customer_token: localStorage.getItem('token') || null
        })
      });
      const data = await response.json();
      if (response.ok && data.success) {
        addBotMessage(data.reply, true, 'ai_buttons');
      } else {
        addBotMessage("I couldn't answer that right now. Please tap 'Talk to human support' or call +91 93199 19988.", true, 'ai_buttons');
      }
    } catch (error) {
      console.error('AI reply error:', error);
      addBotMessage("I couldn't answer that right now. Please tap 'Talk to human support' or call +91 93199 19988.", true, 'ai_buttons');
    } finally {
      setIsLoading(false);
    }
  };

  const renderMessage = (text) => {
    const parts = String(text || '').split(/(\*\*[^*]+\*\*)/g);
    return parts.map((part, i) =>
      part.startsWith('**') && part.endsWith('**')
        ? <strong key={i}>{part.slice(2, -2)}</strong>
        : <React.Fragment key={i}>{part}</React.Fragment>
    );
  };

  const sendMessage = async () => {
    if (!inputMessage.trim()) return;

    const message = inputMessage.trim();
    setInputMessage('');

    // Handle based on current flow step
    if (flowStep === 'location') {
      handleLocationInput(message);
    } else if (flowStep === 'details') {
      handleDetailsFlow(message);
    } else if (flowStep === 'human_chat') {
      // Send to support and refresh messages
      try {
        const response = await fetch(`${API_BASE_URL}/api/chat/send-message`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            session_id: sessionId,
            message: message,
            sender_type: 'visitor'
          })
        });
        
        if (response.ok) {
          // Fetch updated messages from server
          const messagesResponse = await fetch(`${API_BASE_URL}/api/chat/messages/${sessionId}`);
          if (messagesResponse.ok) {
            const data = await messagesResponse.json();
            if (data.success) {
              setMessages(data.messages);
            }
          }
        }
      } catch (error) {
        console.error('Error sending message:', error);
        toast.error('Failed to send message');
      }
    } else {
      // welcome / ai_chat / plan_interest / final → AI assistant answers
      await askAI(message);
    }
  };

  const toggleChat = () => {
    if (!isOpen && !sessionId) {
      startSession();
    }
    setIsOpen(!isOpen);
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-IN', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  return (
    <>
      {/* Floating Chat Button */}
      {!isOpen && (
        <div className="fixed bottom-6 right-6 z-50">
          <button
            onClick={toggleChat}
            data-testid="chat-widget-toggle"
            className="relative bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white rounded-full p-4 shadow-2xl transition-all duration-300 hover:scale-110 group animate-pulse"
            style={{
              boxShadow: '0 10px 40px rgba(139, 92, 246, 0.5)'
            }}
          >
            <MessageCircle className="h-7 w-7" />
            <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-6 w-6 flex items-center justify-center font-bold animate-bounce">
              1
            </span>
            <Sparkles className="absolute -top-2 -left-2 h-4 w-4 text-yellow-400 animate-spin" />
            
            {/* Tooltip */}
            <div className="absolute bottom-full right-0 mb-3 px-4 py-2 bg-gray-900 text-white text-sm rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
              💬 Chat with us!
              <div className="absolute top-full right-4 w-0 h-0 border-l-8 border-r-8 border-t-8 border-transparent border-t-gray-900"></div>
            </div>
          </button>
        </div>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div 
          className="fixed bottom-6 right-6 z-50 w-[420px] max-w-[calc(100vw-48px)] h-[650px] max-h-[calc(100vh-100px)] rounded-3xl shadow-2xl flex flex-col overflow-hidden"
          style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            boxShadow: '0 20px 60px rgba(102, 126, 234, 0.4)'
          }}
        >
          {/* Header */}
          <div className="bg-white/10 backdrop-blur-md text-white p-5 flex items-center justify-between border-b border-white/20">
            <div className="flex items-center gap-3">
              <div className="h-12 w-12 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center relative">
                <Sparkles className="h-6 w-6 text-yellow-300" />
                <span className="absolute -bottom-1 -right-1 h-4 w-4 rounded-full bg-green-400 border-2 border-white animate-pulse"></span>
              </div>
              <div>
                <h3 className="font-bold text-lg">Netix AI Assistant</h3>
                <p className="text-xs text-purple-100 flex items-center gap-1">
                  <span className="h-2 w-2 rounded-full bg-green-400 animate-pulse"></span>
                  Always Here to Help
                </p>
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="hover:bg-white/20 rounded-full p-2 transition-colors"
            >
              <CloseIcon className="h-6 w-6" />
            </button>
          </div>

          {/* Messages Area */}
          <div 
            className="flex-1 overflow-y-auto p-5 space-y-4"
            style={{
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(20px)'
            }}
          >
            {messages.map((msg, index) => (
              <div key={msg.message_id || index}>
                <div
                  className={`flex gap-3 ${
                    msg.sender_type === 'visitor' ? 'justify-end' : 'justify-start'
                  }`}
                >
                  {msg.sender_type !== 'visitor' && (
                    <div className="h-8 w-8 rounded-full bg-gradient-to-r from-purple-500 to-blue-500 flex items-center justify-center flex-shrink-0 shadow-lg">
                      <Bot className="h-5 w-5 text-white" />
                    </div>
                  )}
                  
                  <div
                    className={`max-w-[75%] rounded-2xl px-4 py-3 shadow-lg ${
                      msg.sender_type === 'visitor'
                        ? 'bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-br-none'
                        : 'bg-white text-gray-800 rounded-bl-none'
                    }`}
                    style={{
                      boxShadow: msg.sender_type === 'visitor' 
                        ? '0 4px 15px rgba(139, 92, 246, 0.3)' 
                        : '0 4px 15px rgba(0, 0, 0, 0.1)'
                    }}
                  >
                    <p className="text-sm whitespace-pre-wrap break-words leading-relaxed" data-testid={`chat-message-${msg.sender_type}`}>
                      {renderMessage(msg.message)}
                    </p>
                    <p className={`text-xs mt-1 ${
                      msg.sender_type === 'visitor' ? 'text-purple-100' : 'text-gray-500'
                    }`}>
                      {formatTime(msg.timestamp)}
                    </p>
                  </div>

                  {msg.sender_type === 'visitor' && (
                    <div className="h-8 w-8 rounded-full bg-gradient-to-r from-green-400 to-blue-500 flex items-center justify-center flex-shrink-0 shadow-lg">
                      <User className="h-5 w-5 text-white" />
                    </div>
                  )}
                </div>

                {/* Action Buttons */}
                {msg.showButtons === 'service_type_buttons' && (
                  <div className="mt-3 grid grid-cols-2 gap-3">
                    {['Ask Netix AI', 'New Connection', 'Existing Customer', 'Technical Support', 'Billing'].map((type) => (
                      <button
                        key={type}
                        data-testid={`chat-option-${type.toLowerCase().replace(/\s+/g, '-')}`}
                        onClick={() => handleServiceTypeSelection(type)}
                        className="bg-gradient-to-r from-purple-500 to-blue-500 hover:from-purple-600 hover:to-blue-600 text-white px-4 py-3 rounded-xl font-medium text-sm transition-all duration-300 transform hover:scale-105 shadow-lg"
                      >
                        {type}
                      </button>
                    ))}
                  </div>
                )}

                {msg.showButtons === 'plan_buttons' && (
                  <div className="mt-3 grid grid-cols-2 gap-3">
                    {['50 Mbps - ₹599', '100 Mbps - ₹799', '200 Mbps - ₹999', 'All Plans'].map((plan) => (
                      <button
                        key={plan}
                        onClick={() => handlePlanSelection(plan)}
                        className="bg-gradient-to-r from-green-400 to-blue-500 hover:from-green-500 hover:to-blue-600 text-white px-4 py-3 rounded-xl font-medium text-sm transition-all duration-300 transform hover:scale-105 shadow-lg"
                      >
                        {plan}
                      </button>
                    ))}
                  </div>
                )}

                {msg.showButtons === 'ai_buttons' && index === messages.length - 1 && flowStep !== 'details' && (
                  <div className="mt-2 flex justify-start">
                    <button
                      onClick={handleTalkToHuman}
                      data-testid="chat-talk-to-human-btn"
                      className="text-xs font-semibold text-blue-700 bg-blue-50 hover:bg-blue-100 border border-blue-200 px-3 py-1.5 rounded-full flex items-center gap-1 transition-colors"
                    >
                      <Phone className="h-3 w-3" />
                      Talk to human support
                    </button>
                  </div>
                )}

                {msg.showButtons === 'action_buttons' && (
                  <div className="mt-3 space-y-3">
                    <button
                      onClick={handleCreateTicket}
                      disabled={isLoading}
                      className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white px-5 py-3 rounded-xl font-semibold flex items-center justify-center gap-2 transition-all duration-300 transform hover:scale-105 shadow-xl disabled:opacity-50"
                    >
                      <Ticket className="h-5 w-5" />
                      Create Support Ticket
                      <ArrowRight className="h-4 w-4" />
                    </button>
                    <button
                      onClick={handleChatWithHuman}
                      disabled={isLoading}
                      className="w-full bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white px-5 py-3 rounded-xl font-semibold flex items-center justify-center gap-2 transition-all duration-300 transform hover:scale-105 shadow-xl disabled:opacity-50"
                    >
                      <Phone className="h-5 w-5" />
                      Chat with Human Support
                    </button>
                  </div>
                )}
              </div>
            ))}
            
            {isLoading && (
              <div className="flex justify-start gap-3">
                <div className="h-8 w-8 rounded-full bg-gradient-to-r from-purple-500 to-blue-500 flex items-center justify-center shadow-lg">
                  <Bot className="h-5 w-5 text-white" />
                </div>
                <div className="bg-white rounded-2xl rounded-bl-none px-4 py-3 shadow-lg">
                  <div className="flex gap-2">
                    <span className="h-3 w-3 bg-purple-500 rounded-full animate-bounce"></span>
                    <span className="h-3 w-3 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></span>
                    <span className="h-3 w-3 bg-purple-500 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></span>
                  </div>
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>

          {/* Input Area */}
          <div className="bg-white p-4 border-t border-gray-200">
            <div className="flex gap-2">
              <input
                type="text"
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                placeholder="Ask about plans, coverage, recharge..."
                data-testid="chat-input"
                className="flex-1 px-4 py-3 border-2 border-purple-200 rounded-full focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none transition-all"
                disabled={isLoading}
              />
              <button
                onClick={sendMessage}
                data-testid="chat-send-btn"
                disabled={isLoading || !inputMessage.trim()}
                className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white rounded-full p-3 disabled:opacity-50 disabled:cursor-not-allowed transition-all transform hover:scale-105 shadow-lg"
              >
                <Send className="h-5 w-5" />
              </button>
            </div>
            <div className="mt-2 flex items-center justify-center gap-2 text-xs text-gray-500">
              <Clock className="h-3 w-3" />
              <span>Response time: 30 mins (10 AM - 6 PM)</span>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ChatWidget;
